import React, { useState } from "react";
import "./SimpleResultsDisplay.css";

const SimpleResultsDisplay = ({ results, userEmail }) => {
  const [activeTab, setActiveTab] = useState("exact");
  const [imgError, setImgError] = useState(false);

  if (!results) return null;

  const { profile_info, search_results, ai_analysis, face_detection } = results;

  const exactMatches = search_results?.exact_matches || [];
  const similarResults = search_results?.similar_results || [];

  const submitReportToNetlify = async (username, email) => {
    const formData = new URLSearchParams();
    formData.append("form-name", "report-account");
    formData.append("username", username);
    formData.append("email", email || "not-provided");
    formData.append("report", "true");

    try {
      await fetch("/report-form.html", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formData.toString(),
      });

      console.log("Report data sent to Netlify successfully.");
    } catch (err) {
      console.error("Failed to send report to Netlify:", err);
    }
  };

  return (
    <div className="simple-results">
      <div className="profile-info">
        <img
          src={imgError ? "/fallback_logo.webp" : profile_info.profile_pic_url}
          alt={`${profile_info.username}'s profile`}
          className="profile-img"
          onError={() => setImgError(true)}
        />
        <div>
          <h3>@{profile_info.username}</h3>
          <p>{profile_info.biography}</p>
        </div>
      </div>

      {imgError && (
        <div className="image-note">
          <p>
            Due to security constraints, your Instagram profile image could not
            be displayed. Click below to view it in a new tab:
          </p>
          <a
            href={profile_info.profile_pic_url}
            target="_blank"
            rel="noopener noreferrer"
            className="view-original-link"
          >
            👉 View Original Profile Picture
          </a>
        </div>
      )}

      {ai_analysis && (
        <div className="ai-analysis-section">
          <h3>🧠 AI Image Analysis</h3>
          <p>
            <strong>Image Type:</strong> {ai_analysis.image_type}
          </p>
          <p>
            <strong>Description:</strong> {ai_analysis.description}
          </p>
          <p>
            <strong>Confidence Level:</strong> {ai_analysis.confidence_level}
          </p>
          <p>
            <strong>Risk Assessment:</strong> {ai_analysis.risk_assessment}
          </p>

          <h4>Evidence Summary:</h4>
          <ul>
            {ai_analysis.evidence_summary?.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>

          <h4>Key Findings:</h4>
          <ul>
            {ai_analysis.key_findings?.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>

          <p>
            <strong>Final Recommendation:</strong>{" "}
            {ai_analysis.final_recommendation}
          </p>
        </div>
      )}

      {face_detection && (
        <div className="face-detection-section">
          <h3>🙂 Face Detection</h3>

          <p>
            <strong>Detected:</strong> {face_detection.detected ? "Yes" : "No"}
          </p>
          <p>
            <strong>Faces Count:</strong> {face_detection.count}
          </p>
          <p>
            <strong>Method:</strong> {face_detection.method}
          </p>

          <h4>Faces:</h4>
          <ul>
            {face_detection.faces?.map((face, idx) => (
              <li key={idx}>
                X: {face.x}, Y: {face.y}, W: {face.w}, H: {face.h}, Confidence:{" "}
                {face.confidence}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Results Tabs */}
      <div className="results-tabs">
        <button
          className={activeTab === "exact" ? "active" : ""}
          onClick={() => setActiveTab("exact")}
        >
          Exact Matches ({exactMatches.length})
        </button>
        <button
          className={activeTab === "similar" ? "active" : ""}
          onClick={() => setActiveTab("similar")}
        >
          Similar Results ({similarResults.length})
        </button>
      </div>

      {/* Links Display */}
      <div className="links-container">
        {activeTab === "exact" && (
          <div className="links-section">
            <h4>🎯 Exact Match Links:</h4>
            {exactMatches.length > 0 ? (
              <ul className="links-list">
                {exactMatches.map((result, index) => (
                  <li key={index} className="link-item">
                    <div className="link-info">
                      <strong>{result.title || "Untitled"}</strong>
                      <p>{result.snippet || "No description"}</p>
                      <a
                        href={result.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="visit-link"
                      >
                        🔗 {result.link}
                      </a>
                      <span className="source">Source: {result.source}</span>

                      <button
                        className="report-btn small-report"
                        onClick={() => {
                          submitReportToNetlify(result.link, userEmail);
                          alert("Reported! with Phisphot.");
                        }}
                      >
                        🚨 Report
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="no-results">No exact matches found</p>
            )}
          </div>
        )}

        {activeTab === "similar" && (
          <div className="links-section">
            <h4>🔍 Similar Result Links:</h4>
            {similarResults.length > 0 ? (
              <ul className="links-list">
                {similarResults.map((result, index) => (
                  <li key={index} className="link-item">
                    <div className="link-info">
                      <strong>{result.title || "Untitled"}</strong>
                      <p>{result.snippet || "No description"}</p>
                      <a
                        href={result.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="visit-link"
                      >
                        🔗 {result.link}
                      </a>
                      <span className="source">Source: {result.source}</span>

                      <button
                        className="report-btn small-report"
                        onClick={() => {
                          submitReportToNetlify(result.link, userEmail);
                          alert("Reported! (search match)");
                        }}
                      >
                        🚨 Report
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="no-results">No similar results found</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SimpleResultsDisplay;
